import { useEffect, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
    ArrowLeft, ArrowUpRight, CalendarCheck, MessageCircle, CheckCircle2, Sparkles, Compass,
} from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { BRAND, CONTACT } from "@/data/translations";
import { LangToggle } from "@/components/LangToggle";

const ThankYouHeader = ({ t }) => (
    <header className="border-b border-white/10 bg-black/80 backdrop-blur-xl sticky top-0 z-40">
        <div className="mx-auto max-w-[1100px] px-5 md:px-10 py-5 flex items-center justify-between">
            <Link to="/" data-testid="thankyou-back-link" className="flex items-center gap-3 group">
                <picture>
                    <source srcSet={BRAND.logoDarkWebp} type="image/webp" />
                    <img
                        src={BRAND.logoDark}
                        alt="Fit & Wellness"
                        className="h-9 md:h-10 w-auto"
                        decoding="async"
                        width="120"
                        height="40"
                    />
                </picture>
            </Link>
            <div className="flex items-center gap-4">
                <LangToggle testid="thankyou-language-toggle" />
                <Link
                    to="/"
                    className="hidden sm:inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-white/70 hover:text-fw transition-colors"
                >
                    <ArrowLeft className="w-3.5 h-3.5" strokeWidth={2} />
                    {t.thankYou.backHome}
                </Link>
            </div>
        </div>
    </header>
);

const ProtocolBadge = ({ protocolName }) => {
    if (!protocolName) return null;
    return (
        <div
            data-testid="thankyou-protocol-badge"
            className="mt-6 inline-flex items-center gap-3 px-4 py-3 border border-fw/40 bg-fw/[0.08] rounded-sm"
        >
            <Sparkles className="w-4 h-4 text-fw" strokeWidth={2} />
            <span className="font-mono text-[10px] uppercase tracking-[0.24em] text-white/55">
                Protocol
            </span>
            <span className="font-display text-sm md:text-base font-semibold text-white">
                {protocolName}
            </span>
        </div>
    );
};

const NextStepsCard = ({ t }) => (
    <div className="border border-white/10 bg-white/[0.02] backdrop-blur-sm rounded-sm p-7 md:p-8">
        <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-fw mb-5">
            {t.thankYou.checklistTitle}
        </p>
        <ul className="space-y-4" data-testid="thankyou-checklist">
            {t.thankYou.checklist.map((step, i) => (
                <li key={step} className="flex items-start gap-3">
                    <span className="flex-none mt-0.5 w-6 h-6 rounded-full border border-fw/50 bg-fw/10 flex items-center justify-center font-mono text-[10px] text-fw font-semibold">
                        {i + 1}
                    </span>
                    <span className="text-sm md:text-base text-white/75 leading-relaxed">{step}</span>
                </li>
            ))}
        </ul>
    </div>
);

const ThankYouCTAs = ({ t, protocolSlug }) => (
    <div className="grid grid-cols-1 gap-3">
        <a
            href={CONTACT.calendlyUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="thankyou-cta-calendly"
            className="group relative flex items-center justify-between gap-3 px-6 py-5 bg-fw text-black font-display font-semibold text-[13px] uppercase tracking-[0.18em] rounded-sm hover:bg-fw-glow hover:glow-green transition-all duration-300 overflow-hidden"
        >
            <span className="flex items-center gap-3">
                <CalendarCheck className="w-5 h-5" strokeWidth={2} />
                {t.thankYou.primaryCta}
            </span>
            <ArrowUpRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={2} />
        </a>
        <a
            href={CONTACT.whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="thankyou-cta-whatsapp"
            className="group flex items-center justify-between gap-3 px-5 py-4 border border-white/30 text-white font-display font-semibold text-[12px] uppercase tracking-[0.18em] rounded-sm hover:border-fw hover:text-fw transition-all duration-300"
        >
            <span className="flex items-center gap-2">
                <MessageCircle className="w-4 h-4" strokeWidth={2} />
                {t.thankYou.secondaryCta}
            </span>
            <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={2} />
        </a>
        <Link
            to={protocolSlug ? `/protocollo/${protocolSlug}` : "/#protocols"}
            data-testid="thankyou-cta-protocols"
            className="group inline-flex items-center justify-between gap-3 px-5 py-3 text-white/65 hover:text-fw font-display text-[12px] uppercase tracking-[0.18em] transition-colors"
        >
            <span className="flex items-center gap-2">
                <Compass className="w-4 h-4" strokeWidth={2} />
                {t.thankYou.tertiaryCta}
            </span>
            <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={2} />
        </Link>
    </div>
);

const useThankYouContent = (t, source, name, protocolSlug) => {
    return useMemo(() => {
        if (source === "quiz") {
            const proto = t.protocols.find((p) => p.slug === protocolSlug);
            return {
                title: t.thankYou.quizTitle(name),
                body: t.thankYou.quizBody,
                protocolName: proto?.name || null,
            };
        }
        return {
            title: t.thankYou.resourceTitle(name),
            body: t.thankYou.resourceBody,
            protocolName: null,
        };
    }, [t, source, name, protocolSlug]);
};

export const ThankYou = () => {
    const { t } = useLanguage();
    const [params] = useSearchParams();
    const source = params.get("source") || "resource";
    const name = params.get("name") || "";
    const protocolSlug = params.get("protocollo") || "";

    const { title, body, protocolName } = useThankYouContent(t, source, name, protocolSlug);

    useEffect(() => {
        // Light analytics hook for future PostHog integration (gated by cookie consent).
        if (typeof window !== "undefined" && window.__fwCookieConsent === "accepted") {
            window.dispatchEvent(new CustomEvent("fw:conversion", { detail: { source, protocolSlug } }));
        }
    }, [source, protocolSlug]);

    return (
        <div className="min-h-screen bg-black text-white flex flex-col">
            <ThankYouHeader t={t} />

            <main
                data-testid="thankyou-section"
                className="relative flex-1 overflow-hidden"
            >
                <div className="absolute inset-0 grid-lines opacity-25 pointer-events-none" />
                <div className="absolute inset-0 grain-overlay pointer-events-none" />
                <div className="absolute -top-32 -left-32 w-[480px] h-[480px] rounded-full bg-fw/15 blur-[160px] pointer-events-none" />
                <div className="absolute bottom-0 right-0 w-[520px] h-[520px] rounded-full bg-fw/10 blur-[180px] pointer-events-none" />

                <div className="relative z-10 mx-auto max-w-[1100px] w-full px-5 md:px-10 py-20 md:py-28">
                    <div className="flex items-center gap-3 mb-6">
                        <span className="flex-none w-9 h-9 rounded-full bg-fw/15 border border-fw/40 flex items-center justify-center text-fw">
                            <CheckCircle2 className="w-5 h-5" strokeWidth={2} />
                        </span>
                        <span
                            data-testid="thankyou-overline"
                            className="font-mono text-[11px] uppercase tracking-[0.32em] text-fw"
                        >
                            {t.thankYou.overline}
                        </span>
                    </div>

                    <h1
                        data-testid="thankyou-title"
                        className="font-heavy uppercase leading-[0.98] text-4xl md:text-5xl lg:text-6xl max-w-3xl text-balance mb-6"
                    >
                        {title}
                    </h1>

                    <p
                        data-testid="thankyou-body"
                        className="text-base md:text-lg text-white/75 leading-relaxed max-w-2xl mb-2"
                    >
                        {body}
                    </p>

                    <ProtocolBadge protocolName={protocolName} />

                    <div className="grid grid-cols-12 gap-6 lg:gap-10 mt-12">
                        <div className="col-span-12 lg:col-span-7">
                            <ThankYouCTAs t={t} protocolSlug={protocolSlug} />
                            <p
                                data-testid="thankyou-social-proof"
                                className="mt-6 font-mono text-[10px] uppercase tracking-[0.24em] text-white/45"
                            >
                                {t.thankYou.socialProof}
                            </p>
                        </div>
                        <div className="col-span-12 lg:col-span-5">
                            <NextStepsCard t={t} />
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
};

export default ThankYou;
